import React, { useContext, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { ArrowUpRight, Bot, CoinsIcon } from "lucide-react";
import { CreditsContext } from "@/store/credits.store";

/**
 * Model usage data interface
 */
interface ModelUsage {
  /** Model name */
  name: string;
  /** Used credits percentage (null if unlimited) */
  used: number | null;
  /** Total credits percentage (null if unlimited) */
  total: number | null;
  /** Whether model has unlimited credits */
  unlimited?: boolean;
}

/**
 * ModelUsageDropdown component displays a dropdown menu showing model usage
 * statistics with progress bars. Shows used/total credits for each model group
 * or "Unlimited" for models with unlimited credits. Opens credits page on click.
 * 
 * @component
 * @returns {JSX.Element} Dropdown menu with model usage statistics
 * 
 * @example
 * ```tsx
 * <ModelUsageDropdown />
 * ```
 */
export default function ModelUsageDropdown() {
  const { state } = useContext(CreditsContext);

  const models: ModelUsage[] = useMemo(() => {
    if (!state.model_groups?.length) return [];

    return state.model_groups.map((mg: any) => {
      const monthly = mg.monthly_credits;
      const usedTokens = mg.usage?.used_tokens ?? 0;

      // Unlimited handling
      if (monthly === -1) {
        return {
          name: mg.name,
          used: null,        
          total: null,
          unlimited: true,  
        };
      }

      // Normalize to 100
      const usedPct =
        monthly > 0 ? Math.round((usedTokens / monthly) * 100) : 0;

      return {
        name: mg.name,
        used: usedPct,
        total: 100,
        unlimited: false,
      };
    });
  }, [state.model_groups]);

  return (
    <div className="flex w-full items-center p-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="
              flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer ml-1
              focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none
            "
          >
            <span className="flex items-center gap-2">
              <CoinsIcon size={16} />
              credits
            </span>

            <ArrowUpRight size={16} />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="top"
          className="min-w-56 rounded-xl shadow-lg border z-[101] p-0"
        >
          {models.map((m, index) => (
            <div key={m.name}>
              <DropdownMenuItem
                className="flex flex-col items-start px-3 py-2 cursor-default"
              >
                <div className="flex items-center gap-3 w-full">
                  <Bot className="text-primary-500" size={18} />

                  <div className="flex flex-col">
                    <span className="font-medium text-primary-500">
                      {m.name}
                    </span>

                    {m.unlimited ? (
                      <span className="text-xs text-green-700 font-medium">
                        Unlimited
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">
                        {m.used} / {m.total} credits used
                      </span>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
              {index+1 != models.length && <DropdownMenuSeparator className="m-0"/>}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
